import React, { Suspense, Component, ReactNode } from 'react';
import { usePluginRegistry } from '../plugins/registry.js';
import { Loader2, AlertTriangle } from 'lucide-react';

interface DashboardLayoutProps {
  activePluginId?: string;
  className?: string;
}

interface PluginErrorBoundaryProps {
  children: ReactNode;
  pluginName: string;
}

interface PluginErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class PluginErrorBoundary extends Component<PluginErrorBoundaryProps, PluginErrorBoundaryState> {
  constructor(props: PluginErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): PluginErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Plugin error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center p-8 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="text-center">
            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-red-400 mb-2">Plugin Error</h3>
            <p className="text-red-300 text-sm">
              Failed to load {this.props.pluginName} plugin. Please try refreshing the page.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const PluginLoader: React.FC<{ pluginId: string }> = ({ pluginId }) => {
  const registry = usePluginRegistry();
  const plugin = registry.getPlugin(pluginId);

  if (!plugin) {
    return (
      <div className="flex items-center justify-center p-8 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-yellow-400 mb-2">Plugin Not Found</h3>
          <p className="text-yellow-300 text-sm">
            Plugin "{pluginId}" could not be found in the registry.
          </p>
        </div>
      </div>
    );
  }

  if (!plugin.isEnabled) {
    return (
      <div className="flex items-center justify-center p-8 bg-gray-500/10 border border-gray-500/20 rounded-lg">
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-400 mb-2">Plugin Disabled</h3>
          <p className="text-gray-300 text-sm">
            The {plugin.manifest.name} plugin is currently disabled.
          </p>
        </div>
      </div>
    );
  }

  const PluginComponent = plugin.component;

  return (
    <PluginErrorBoundary pluginName={plugin.manifest.name}>
      <Suspense
        fallback={
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
            <span className="ml-3 text-slate-400">Loading {plugin.manifest.name}...</span>
          </div>
        }
      >
        <PluginComponent isActive={true} />
      </Suspense>
    </PluginErrorBoundary>
  );
};

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ 
  activePluginId, 
  className = '' 
}) => {
  const registry = usePluginRegistry();

  if (activePluginId) {
    return (
      <div className={`dashboard-layout h-full w-full ${className}`}>
        <PluginLoader pluginId={activePluginId} />
      </div>
    );
  }

  return (
    <div className={`dashboard-layout h-full w-full ${className}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 p-6">
        {registry.enabledPlugins.map(plugin => (
          <div key={plugin.manifest.id} className="plugin-card">
            <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-violet-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {plugin.manifest.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{plugin.manifest.name}</h3>
                  <p className="text-sm text-slate-400">v{plugin.manifest.version}</p>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-4">{plugin.manifest.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded">
                  Enabled
                </span>
                <button
                  onClick={() => window.location.href = plugin.manifest.route}
                  className="text-violet-400 hover:text-violet-300 text-sm font-medium"
                >
                  Open →
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {registry.enabledPlugins.length === 0 && (
          <div className="col-span-full flex items-center justify-center p-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-700 rounded-lg flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-300 mb-2">No Plugins Available</h3>
              <p className="text-slate-400 text-sm">
                No plugins are currently enabled or available in the registry.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardLayout;
